import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Swal from 'sweetalert2';
import { useSnackbar } from 'notistack';

import {
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import { MenuService } from '../../Services/menu.service';
import { useLoadingContext } from '../../context/loadingContext';

const Menu = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [show, setShow] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [menu, setMenu] = useState([]);
  const [menuId, setMenuId] = useState('');
  const { handleStartLoading, handleStopLoading } = useLoadingContext()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    getMenu().finally(handleStopLoading);
  }, []);

  const getMenu = async () => {
    handleStartLoading()
    const menu = await MenuService.GetMenu();
    setMenu(menu);
  };

  const handleCreateMenu = () => {
    setShow(true);
  };

  const handleEditClick = (id, name, check) => {
    setMenuId(id);
    setShow(true);
    setName(name);
    setIsMobile(check)
    setEditMode(true);
  };

  const handleClose = () => {
    setName('');
    setEditMode(false);
    setShow(false);
  };

  const handleCreateSubmit = () => {
    if (name === '') {
      return enqueueSnackbar('Tên không được để trống', { variant: 'error' });
    }
    handleStartLoading()
    MenuService.CreateMenu({ name, isMobile })
      .then((res) => {
        setMenu((prevState) => [...prevState, res]);
        enqueueSnackbar('Thêm menu thành công');
        handleClose();
      })
      .catch((err) => enqueueSnackbar(err.response.data))
      .finally(handleStopLoading)
  };

  const handleEditSubmit = () => {
    if (name === '') {
      return enqueueSnackbar('Tên không được để trống', { variant: 'error' });
    }
    handleStartLoading()
    MenuService.EditMenu(menuId, { name, isMobile })
      .then(() => {
        setMenu((prevState) =>
          prevState.map((menu) => {
            if (menu._id === menuId) {
              menu.menuName = name;
              menu.isMobile = isMobile
            }
            return menu;
          })
        );
        enqueueSnackbar('Cập nhật thành công', { variant: 'success' });
        handleClose();
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading)
  };

  const handleDeleteMenu = (id) => {
    Swal.fire({
      customClass: {
        container: { zIndex: 999999 },
      },
      title: 'Thao tác này không  thể hoàn tác',
      text: 'Bạn chắc chắn muốn xóa menu này',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy',
    }).then((result) => {
      if (result.isConfirmed) {
        handleStartLoading()
        MenuService.DeleteMenu(id)
          .then(() => {
            setMenu((prevState) => prevState.filter((menu) => menu._id !== id));
            handleClose();
            Swal.fire('Thành công', ' Xóa menu thành công', 'success');
          })
          .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
          .finally(handleStopLoading);
      }
    });
  };

  return (
    <Page title="Menu">
      <Dialog open={show} fullWidth maxWidth="sm" onClose={handleClose}>
        <DialogTitle>Menu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            value={name}
            onChange={(e) => setName(e.target.value)}
            id="name"
            label="Tên menu"
            fullWidth
            variant="standard"
          />
          <FormGroup>
            <FormControlLabel control={<Checkbox checked={isMobile}
              onChange={e => setIsMobile(e.target.checked)} />} label="Mobile menu" />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="outlined" onClick={handleClose}>
            Hủy
          </Button>
          <Button variant={'outlined'} onClick={editMode ? handleEditSubmit : handleCreateSubmit}>
            {editMode ? 'Cập  nhật' : 'Thêm menu'}
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            Menu
          </Typography>

          <Button onClick={handleCreateMenu} variant="contained" startIcon={<Iconify icon="akar-icons:plus" />}>
            Thêm menu
          </Button>
        </Stack>
        <Divider sx={{ my: 5 }} />
        {menu.map((item) => (
          <Card sx={{ mb: 2, p: 2, alignItems: 'center' }} key={item._id}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h4" gutterBottom>
                  {item.menuName} {item.isMobile && (<Chip label="Mobile" variant="outlined" color="primary" size="small" />)}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button color="error" size="small" onClick={() => handleDeleteMenu(item._id)} variant="outlined">
                  Xóa
                </Button>
                <Button
                  color="info"
                  size="small"
                  onClick={() => handleEditClick(item._id, item.menuName, item.isMobile)}
                  variant="outlined"
                >
                  Cập nhật
                </Button>
                <Button color="primary" size="small" onClick={() => navigate(item._id)} variant="outlined">
                  Chi tiết
                </Button>
              </Stack>
            </Stack>
          </Card>
        ))}
      </Container>
    </Page>
  );
};

export default Menu;
